import React, { useEffect, useMemo, useRef, useState } from "react"
import {
  Button,
  Card,
  CardBody,
  CardTitle,
  Col,
  Row,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from "reactstrap"
import classnames from "classnames"
import { Form, Formik } from "lib/formik"
import Breadcrumbs from "components/Common/Breadcrumb"
import BackButton from "components/Shared/BackButton"
import { useLocation, useNavigate } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import {
  platformCountrySchema,
  tenantCountryTaxShipmentSchema,
} from "components/Common/validation"
import {
  addCountry,
  getCountryDetails,
  updateCountry,
  updateTenantCountryTaxShipment,
} from "store/actions/countriesAction"
import { showAlert } from "store/reducers/alertReducer"
import Spinners from "components/Shared/Spinners"
import isPermissionEnabled from "common/isPermissionEnabled"
import { isPlatformCountryManager } from "utils/platformCountryAccess"
import {
  getCountriesBasePath,
  getCountriesFallbackDashboard,
} from "utils/countriesRoutes"
import { useTranslation } from "react-i18next"
import CountryFormDetails from "./components/CountryFormDetails"
import CountryTaxDetails from "./components/CountryTaxDetails"
import { getAllBranchsList } from "store/actions/branchsActions"
import { getCrossModuleFilterPermissions } from "utils/crossModuleFilterPermissions"
// Shipment Details temporarily disabled — not used in live delivery fee logic.
// import { getAllZoneList } from "store/actions/zoneAction"
// import CountryShipmentDetail from "./components/CountryShipmentDetail"

const initialValues = {
  name: "",
  nameAr: "",
  dialCode: "",
  countryCode: "",
  displayOrder: "",
  phoneNumberLength: "",
  title: "",
  titleAr: "",
  value: "",
  registeredNo: "",
  currency: "",
  status: 1,
  currencySymbol: "",
  // Shipment Details temporarily disabled.
  // regionType: "",
  // fixedDeliveryCharge: "",
  // freeDeliveryThreshold: "",
  // enableFreeDelivery: false,
  // actualBaseWeightLimit: "",
  // actualBaseRate: "",
  // actualExtraPerKgRate: "",
  // dynamicBaseWeightLimit: "",
  // dynamicBaseRate: "",
  // dynamicExtraPerKgRate: "",
  // enableBoxFee: false,
  // boxFee: "",
  // noOfBaseItemsLimit: "",
  // baseFulfillmentFee: "",
  // extraItemFee: "",
  // enableSurcharge: false,
  // surchargeRate: "",
  // enableDynamicCalc: false,
  // volumetricDivisor: "",
  // priorityLogic: "actual_weight",
  isCountryLocal: false,
}

const COUNTRY_DETAILS_FIELDS = [
  "name",
  "nameAr",
  "countryCode",
  "dialCode",
  "displayOrder",
  "phoneNumberLength",
  "currency",
  "currencySymbol",
]
const COUNTRY_TAX_FIELDS = [
  "title",
  "titleAr",
  "value",
  "registeredNo",
]
// Shipment Details temporarily disabled.
// const COUNTRY_SHIPMENT_FIELDS = [
//   "regionType",
//   "fixedDeliveryCharge",
//   "freeDeliveryThreshold",
//   "enableFreeDelivery",
//   "actualBaseWeightLimit",
//   "actualBaseRate",
//   "actualExtraPerKgRate",
//   "dynamicBaseWeightLimit",
//   "dynamicBaseRate",
//   "dynamicExtraPerKgRate",
//   "enableBoxFee",
//   "boxFee",
//   "noOfBaseItemsLimit",
//   "baseFulfillmentFee",
//   "extraItemFee",
//   "enableSurcharge",
//   "surchargeRate",
//   "enableDynamicCalc",
//   "volumetricDivisor",
//   "priorityLogic",
// ]

const resolveCountryFormTabForErrors = (errors = {}, { isPlatform } = {}) => {
  const errorFields = Object.keys(errors || {})
  if (
    isPlatform &&
    errorFields.some(field => COUNTRY_DETAILS_FIELDS.includes(field))
  ) {
    return "1"
  }
  if (errorFields.some(field => COUNTRY_TAX_FIELDS.includes(field))) {
    return "2"
  }
  // if (errorFields.some(field => COUNTRY_SHIPMENT_FIELDS.includes(field))) {
  //   return "3"
  // }
  return null
}

/** When submit fails validation, jump to the tab with errors. */
const CountryFormValidationWatcher = ({
  submitCount,
  errors,
  setActiveTab,
  isPlatform,
}) => {
  const lastHandledSubmitCount = useRef(0)

  useEffect(() => {
    if (submitCount <= lastHandledSubmitCount.current) return
    lastHandledSubmitCount.current = submitCount

    const errorKeys = Object.keys(errors || {})
    if (!errorKeys.length) return

    const tab = resolveCountryFormTabForErrors(errors, { isPlatform })
    if (tab) setActiveTab(tab)
  }, [submitCount, errors, setActiveTab, isPlatform])

  return null
}

const AddCountry = ({ mode }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation()
  document.title = `${
    mode === "add" ? t("Add") : t("Edit")
  } Country `

  const location = useLocation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const searchParams = new URLSearchParams(location.search)
  const id = searchParams.get("id")

  const { loading, isLoading, countryDetails } = useSelector(
    state => state.Countries
  )
  const { userRole, accountType, accountScope } = useSelector(
    state => state.Login
  )
  const { canFilterByBranch } = getCrossModuleFilterPermissions(
    userRole,
    accountType,
    accountScope
  )
  const isPlatform = isPlatformCountryManager({ accountType, accountScope })
  const countriesBasePath = getCountriesBasePath({ accountType, accountScope })

  const [formValues, setFormValues] = useState(initialValues)
  const [activeTab, setActiveTab] = useState(isPlatform ? "1" : "2")

  const { branchsList } = useSelector(state => state.Branchs)
  // const { zoneList } = useSelector(state => state.Zone)

  useEffect(() => {
    if (!canFilterByBranch) return
    const payload = {
      perPage: 10000,
      pageNo: 1,
      status: 2,
      orderBy: 1,
      keyword: "",
    }
    dispatch(getAllBranchsList(payload))
    // dispatch(getAllZoneList())
  }, [canFilterByBranch, dispatch])

  useEffect(() => {
    const canEditTaxShipment = isPermissionEnabled(
      userRole,
      11,
      "country_5",
      accountType,
      accountScope
    )
    // Platform: full create/edit of country identity. Tenant: edit tax only
    // (shipment details temporarily disabled).
    const canCreate = isPlatform
    const canEdit = isPlatform || canEditTaxShipment

    if ((mode === "add" && !canCreate) || (mode === "edit" && !canEdit)) {
      navigate(getCountriesFallbackDashboard({ accountType, accountScope }))
      return
    }

    if (!isPlatform && mode === "edit") {
      setActiveTab("2")
    }

    if (mode === "edit") {
      dispatch(getCountryDetails({ id }))
    }
  }, [mode, userRole, accountType, accountScope, isPlatform, dispatch, id, navigate])

  useEffect(() => {
    if (mode !== "edit" || loading || !countryDetails?._id) return

    const {
      taxInfo,
      name,
      nameAr,
      countryCode,
      dialCode,
      status,
      displayOrder,
      phoneNumberLength,
      currency,
      currencySymbol,
      // shipmentInfo,
    } = countryDetails

    let updatedBranch = {}
    const matchedBranch = branchsList?.find(
      item => item._id == taxInfo?.branch
    )
    if (matchedBranch) {
      updatedBranch = {
        label:
          language === "sa" ? matchedBranch?.nameAr : matchedBranch?.name,
        value: matchedBranch?._id,
      }
    }

    setFormValues(prev => ({
      ...prev,
      name: name ?? "",
      nameAr: nameAr ?? "",
      dialCode: dialCode ?? "",
      countryCode: String(countryCode || "").toUpperCase(),
      currencySymbol: currencySymbol ?? "",
      displayOrder: displayOrder ?? "",
      phoneNumberLength: phoneNumberLength ?? "",
      title: taxInfo?.title ?? "",
      titleAr: taxInfo?.titleAr ?? "",
      value: taxInfo?.value ?? "",
      registeredNo: taxInfo?.registeredNo ?? "",
      branch: updatedBranch,
      currency: currency ?? "",
      status: status ?? 1,
      // Shipment Details temporarily disabled.
      // ...(shipmentInfo || {}),
      // priorityLogic: shipmentInfo?.priorityLogic || "actual_weight",
    }))
  }, [branchsList, countryDetails, language, loading, mode])

  const toggleTab = tab => {
    if (activeTab !== tab) setActiveTab(tab)
  }

  const handleOnSubmit = values => {
    // Keep values if the form remounts behind the save spinner.
    setFormValues(values)

    const {
      name,
      nameAr,
      dialCode,
      countryCode,
      displayOrder,
      phoneNumberLength,
      title,
      titleAr,
      value,
      registeredNo,
      currency,
      status,
      currencySymbol,
      branch,
      // Shipment Details temporarily disabled.
      // regionType,
      // fixedDeliveryCharge,
      // freeDeliveryThreshold,
      // enableFreeDelivery,
      // actualBaseWeightLimit,
      // actualBaseRate,
      // actualExtraPerKgRate,
      // dynamicBaseWeightLimit,
      // dynamicBaseRate,
      // dynamicExtraPerKgRate,
      // enableBoxFee,
      // boxFee,
      // noOfBaseItemsLimit,
      // baseFulfillmentFee,
      // extraItemFee,
      // enableSurcharge,
      // surchargeRate,
      // enableDynamicCalc,
      // volumetricDivisor,
      // priorityLogic,
    } = values

    const taxInfo = {
      title,
      titleAr,
      value,
      registeredNo,
      branch: branch?.value,
    }
    // const shipmentInfo = {
    //   regionType,
    //   fixedDeliveryCharge,
    //   freeDeliveryThreshold,
    //   enableFreeDelivery,
    //   actualBaseWeightLimit,
    //   actualBaseRate,
    //   actualExtraPerKgRate,
    //   dynamicBaseWeightLimit,
    //   dynamicBaseRate,
    //   dynamicExtraPerKgRate,
    //   enableBoxFee,
    //   boxFee,
    //   noOfBaseItemsLimit,
    //   baseFulfillmentFee,
    //   extraItemFee,
    //   enableSurcharge,
    //   surchargeRate,
    //   enableDynamicCalc,
    //   volumetricDivisor,
    //   priorityLogic,
    // }

    let action
    if (!isPlatform) {
      action = updateTenantCountryTaxShipment({
        countryId: id,
        taxInfo,
        // shipmentInfo,
      })
    } else {
      const payload = {
        name,
        nameAr,
        dialCode,
        countryCode: String(countryCode || "").toUpperCase(),
        displayOrder,
        phoneNumberLength: Number(phoneNumberLength),
        currency,
        currencySymbol,
      }
      action =
        mode === "add"
          ? addCountry(payload)
          : updateCountry({ ...payload, id, status: Number(status) })
    }

    dispatch(action)
      .then(({ payload }) => {
        const { statusCode, status, msg } = payload
        dispatch(showAlert({ text: msg, type: status ? "success" : "error" }))
        if (statusCode === 200 && status) {
          navigate(mode === "add" ? countriesBasePath : -1)
        }
      })
      .catch(() => {})
  }

  const validationSchema = useMemo(
    () =>
      isPlatform ? platformCountrySchema() : tenantCountryTaxShipmentSchema(),
    [isPlatform]
  )

  if ((mode === "edit" && loading) || isLoading) return <Spinners />

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs />
        <Row>
          <Col xl={12}>
            <Card>
              <CardBody>
                <CardTitle className="mb-2 d-flex align-items-center">
                  <BackButton />
                  {mode === "add" ? t("Add") : t("Edit")} {t("Country")}
                </CardTitle>

                <Nav tabs>
                  {isPlatform && (
                    <NavItem>
                      <NavLink
                        className={classnames({ active: activeTab === "1" })}
                        onClick={() => toggleTab("1")}
                        style={{ cursor: "pointer" }}
                      >
                        {t("Actual Details")}
                      </NavLink>
                    </NavItem>
                  )}
                  {!isPlatform && (
                    <>
                      <NavItem>
                        <NavLink
                          className={classnames({ active: activeTab === "2" })}
                          onClick={() => toggleTab("2")}
                          style={{ cursor: "pointer" }}
                        >
                          {t("Tax Details")}
                        </NavLink>
                      </NavItem>
                      {/* Shipment Details temporarily disabled. */}
                      {/* <NavItem>
                        <NavLink
                          className={classnames({ active: activeTab === "3" })}
                          onClick={() => toggleTab("3")}
                          style={{ cursor: "pointer" }}
                        >
                          {t("Shipment Details")}
                        </NavLink>
                      </NavItem> */}
                    </>
                  )}
                </Nav>

                <Formik
                  initialValues={formValues}
                  enableReinitialize={mode === "edit"}
                  validationSchema={validationSchema}
                  onSubmit={handleOnSubmit}
                >
                  {({
                    touched,
                    errors,
                    setFieldValue,
                    values,
                    submitCount,
                  }) => (
                    <Form>
                      <CountryFormValidationWatcher
                        submitCount={submitCount}
                        errors={errors}
                        setActiveTab={setActiveTab}
                        isPlatform={isPlatform}
                      />
                      <TabContent activeTab={activeTab}>
                        {isPlatform && (
                          <TabPane tabId="1">
                            <Row>
                              <CountryFormDetails
                                t={t}
                                errors={errors}
                                touched={touched}
                                submitCount={submitCount}
                                setFieldValue={setFieldValue}
                              />
                            </Row>
                          </TabPane>
                        )}

                        {!isPlatform && (
                          <>
                            <TabPane tabId="2">
                              <Row>
                                <CountryTaxDetails
                                  t={t}
                                  language={language}
                                  errors={errors}
                                  touched={touched}
                                  submitCount={submitCount}
                                  setFieldValue={setFieldValue}
                                  branches={branchsList}
                                  values={values}
                                />
                              </Row>
                            </TabPane>
                            {/* Shipment Details temporarily disabled. */}
                            {/* <TabPane tabId="3">
                              <Row>
                                <CountryShipmentDetail
                                  t={t}
                                  errors={errors}
                                  touched={touched}
                                  submitCount={submitCount}
                                  language={language}
                                  setFieldValue={setFieldValue}
                                  values={values}
                                  zoneList={zoneList}
                                />
                              </Row>
                            </TabPane> */}
                          </>
                        )}
                      </TabContent>

                      <div className="d-flex justify-content-end mt-5 mb-4">
                        <Button
                          type="button"
                          className="mx-2"
                          color="secondary"
                          onClick={() => navigate(-1)}
                        >
                          {t("Cancel")}
                        </Button>
                        <Button
                          type="submit"
                          color="success"
                          disabled={isLoading}
                          className="w_fit_content"
                        >
                          {mode === "add" ? t("Add") : t("Save")}
                        </Button>
                      </div>
                    </Form>
                  )}
                </Formik>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  )
}

export default AddCountry
